import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import api, { uploadAvatar, getEvidenceDisplayUrl } from '../../../lib/api';
import { useAuthStore } from '../../../store/authStore';
import SettingsCard from '../../../components/ui/SettingsCard';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import AvatarUploader from '../../../components/ui/AvatarUploader';
import { compressAvatarImage } from '../../../lib/imageCompression';

function pickProfile(u) {
  if (!u) return { firstName: '', lastName: '', email: '', phone: '', electricalLicenceNo: '' };
  return {
    firstName: (u.firstName ?? u.first_name ?? '').toString(),
    lastName: (u.lastName ?? u.last_name ?? '').toString(),
    email: (u.email ?? '').toString(),
    phone: (u.phone ?? '').toString(),
    electricalLicenceNo: (u.electricalLicenceNo ?? u.electrical_licence_no ?? '').toString(),
  };
}

export default function ProfileSettings() {
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);
  const queryClient = useQueryClient();
  const userId = user?.id ?? user?.userId;

  const { data: profileData, error: profileError } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => api.get('/auth/me').then((res) => res.data),
    staleTime: 60 * 1000,
  });

  const [profile, setProfile] = useState(() => pickProfile(user));
  const [avatarUrl, setAvatarUrl] = useState(() => user?.avatarUrl ?? '');
  const [avatarBusy, setAvatarBusy] = useState(false);
  useEffect(() => {
    const source = profileData ?? user;
    setProfile(pickProfile(source));
    setAvatarUrl(source?.avatarUrl ?? source?.avatar_url ?? '');
  }, [profileData, user]);

  const initials = `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.trim();

  const handleAvatarUpload = async (file) => {
    setAvatarBusy(true);
    try {
      // Small JPEG: a phone photo posted raw is rejected by the 2 MB avatar
      // endpoint (and refused outright if it is HEIC). Not the brand PNG path —
      // a photographic PNG at this size can approach the cap and fail
      // intermittently, and an avatar has no transparency to preserve.
      const prepared = await compressAvatarImage(file);
      const url = await uploadAvatar(prepared);
      setAvatarUrl(url);
      updateUser({ avatarUrl: url });
      queryClient.invalidateQueries(['auth-me']);
      toast.success('Photo updated');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Photo upload failed');
    } finally {
      setAvatarBusy(false);
    }
  };

  const handleAvatarRemove = async () => {
    if (!userId) return;
    setAvatarBusy(true);
    try {
      await api.put(`/users/${userId}/profile`, { avatarUrl: null });
      setAvatarUrl('');
      updateUser({ avatarUrl: null });
      queryClient.invalidateQueries(['auth-me']);
      toast.success('Photo removed');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to remove photo');
    } finally {
      setAvatarBusy(false);
    }
  };

  const profileMutation = useMutation(
    (data) => api.put(`/users/${userId}/profile`, data).then((res) => res.data),
    {
      onSuccess: (u) => {
        updateUser({
          firstName: u.firstName,
          lastName: u.lastName,
          email: u.email,
          phone: u.phone,
          electricalLicenceNo: u.electricalLicenceNo,
        });
        setProfile(pickProfile(u));
        queryClient.invalidateQueries(['auth-me']);
        toast.success('Profile updated');
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Failed to update profile');
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!userId) return;
    profileMutation.mutate({
      firstName: profile.firstName.trim(),
      lastName: profile.lastName.trim(),
      email: profile.email.trim(),
      phone: profile.phone.trim() || null,
      electricalLicenceNo: profile.electricalLicenceNo.trim() || null,
    });
  };

  const setField = (field) => (e) =>
    setProfile((p) => ({ ...p, [field]: e.target.value }));

  return (
    <div className="page settings-page">
      <header className="settings-tab-header">
        <h1 className="settings-tab-title">Profile</h1>
        <p className="text-muted">Your personal details, used across FieldCert and on reports.</p>
        {profileError && (
          <p className="text-muted">Showing locally cached details — could not refresh from the server.</p>
        )}
      </header>

      <SettingsCard title="Photo" hint="A profile photo helps your team recognise you.">
        <AvatarUploader
          shape="circle"
          label="photo"
          alt="Profile photo"
          initials={initials}
          src={getEvidenceDisplayUrl(avatarUrl)}
          busy={avatarBusy}
          onUpload={handleAvatarUpload}
          onRemove={handleAvatarRemove}
        />
      </SettingsCard>

      <SettingsCard title="My Profile">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <Input
              id="profile-first-name"
              label="First name"
              type="text"
              value={profile.firstName}
              onChange={setField('firstName')}
            />
            <Input
              id="profile-last-name"
              label="Last name"
              type="text"
              value={profile.lastName}
              onChange={setField('lastName')}
            />
          </div>
          <Input
            id="profile-email"
            label="Email"
            type="email"
            value={profile.email}
            onChange={setField('email')}
            required
          />
          <div className="form-row">
            <Input
              id="profile-phone"
              label="Phone"
              type="tel"
              value={profile.phone}
              onChange={setField('phone')}
              placeholder="e.g. 0400 000 000"
            />
            <Input
              id="profile-licence"
              label="Electrical licence no."
              type="text"
              value={profile.electricalLicenceNo}
              onChange={setField('electricalLicenceNo')}
              placeholder="e.g. 123456C"
            />
          </div>
          <div className="form-actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setProfile(pickProfile(profileData ?? user))}
              disabled={profileMutation.isLoading}
            >
              Discard
            </Button>
            <Button type="submit" variant="primary" disabled={profileMutation.isLoading || !userId}>
              {profileMutation.isLoading ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </SettingsCard>
    </div>
  );
}
